import { useUserIdWithLoading } from '@dreamstack/auth'
import { useLocale } from '@dreamstack/i18n'
import { useTrackFormSubmissionGA4 } from '@dreamstack/tracking'
import { getOrCreateAnonymousId } from '@dreamstack/tracking/src/getOrCreateAnonymousId'
import { trpc } from '@dreamstack/trpc/client'
import type { RouterInputs } from '@dreamstack/trpc/src/client/trpc'
import type { ExtendedSendContactFormInput } from '@dreamstack/web/pages/api/hasura/sendContactForm'
import axios from 'axios'
import { useCallback } from 'react'
import {
  IS_ACCENTRO_INTERNATIONAL,
  IS_ACCENTRO_INVESTORS,
  IS_ACCENTRO_WEB,
  NewsletterType,
} from '../index'
import { getGoogleAnalyticsIds } from '../utils/getGoogleAnalyticsIds'
import type { contactFormType } from './typings'
import type { ContactFormData } from './useContactFormSchema'
import type { Is24ContactFormData } from './useIs24ContactFormSchema'
import type { PropAndProjectContactFormData } from './usePropAndProjectContactFormSchema'

type SendContactFormInput = RouterInputs['contact']['sendContactForm']

const parseDataForMutation = ({
  data,
}: {
  data:
    | (Is24ContactFormData & {
        type: contactFormType
      })
    | (ContactFormData & {
        type: contactFormType
      })
    | (PropAndProjectContactFormData & {
        type: contactFormType
      })
}): SendContactFormInput => {
  const res: ExtendedSendContactFormInput = { ...data }
  switch (data.type) {
    case 'contactform':
      const typedContactData = data as ContactFormData
      if (
        typedContactData.clientStreet &&
        typedContactData.clientStreetnumber
      ) {
        res.clientStreet = `${typedContactData.clientStreet} ${typedContactData.clientStreetnumber}`
      }
      if (typedContactData.subject) {
        res.subject = `Kontaktanfrage - ${typedContactData.subject}`
      }
      break

    case 'is24contactform':
    case 'propandprojectcontactform':
      const typedData = data as
        | Is24ContactFormData
        | PropAndProjectContactFormData
      if (typedData.requestPhone) {
        res.clientPhone = `Rückruf gewünscht: ${data.clientPhone}`
      }

      break

    default:
      break
  }
  return res
}

export const useSubmitContactForm = ({
  propertyId,
  projectId,
  successCallback,
  formId,
  customSubject,
  type,
}: {
  projectId?: number
  propertyId?: number
  formId: string
  type: contactFormType
  successCallback?: () => void
  customSubject?: string
}) => {
  const { userId } = useUserIdWithLoading()

  const trackFormSubmissionForGA4 = useTrackFormSubmissionGA4()

  const { mutateAsync: sendContactFormMutation, isLoading: loading } =
    trpc.contact.sendContactForm.useMutation()

  const { mutateAsync: sendNewsletterRegistration } =
    trpc.newsletter.sendRegisterNewsletter.useMutation()
  const { mutateAsync: trackFormSubmissionServer } =
    trpc.anonymousTracking.trackEvent.useMutation()

  const locale = useLocale()

  const submitFn = useCallback(
    async (data: ContactFormData) => {
      let variables = parseDataForMutation({ data: { ...data, type } })
      const anonymousUserId = getOrCreateAnonymousId()
      const gaIds = await getGoogleAnalyticsIds()
      if (gaIds) {
        variables = {
          ...variables,
          ...gaIds,
        }
      }

      if (data.subscribeNewsletter && data.clientEmail) {
        sendNewsletterRegistration({
          email: data.clientEmail,
          type: NewsletterType.Newsletter,
          locale,
          wantsNewsletter: true,
        })
        trackFormSubmissionServer({
          eventName: 'formSubmission',
          userId,
          anonymousUserId,
          data: {
            formId: 'Registered for Newsletter',
            formData: {
              email: data.clientEmail,
            },
          },
        })
      }

      if (data.subscribeHomeOwnerShipReport) {
        const endpoint = process.env.NEXT_PUBLIC_GRAPHQL_API
        const headers = {
          'content-type': 'application/json',
        }
        const graphqlQuery = {
          query: `mutation sendHomeOwnershipReport($email: String!, $locale: String!) {
            sendHomeOwnershipReport(email: $email, locale: $locale) {
              success
            }
          }`,
          variables: { email: data.clientEmail, locale },
        }

        axios({
          url: endpoint,
          method: 'post',
          headers: headers,
          data: graphqlQuery,
        })
      }

      let isSuccess = false
      if (IS_ACCENTRO_INVESTORS || IS_ACCENTRO_INTERNATIONAL) {
        const { data } = await axios.post('/api/sendContactForm', variables)
        isSuccess = data?.result?.status === 'success'
      } else if (IS_ACCENTRO_WEB) {
        if (propertyId) {
          variables.propertyId = +propertyId
        }
        if (projectId) {
          variables.projectId = +projectId
        }
        if (!data.clientPhone) {
          variables.clientPhone = 'nicht angegeben'
        }
        if (customSubject) {
          variables.subject = customSubject
        }

        const resultData = await sendContactFormMutation(variables)
        isSuccess = !!resultData?.success
        const trackingData = {
          formId,
          formData: {
            ...variables,
            isSuccess,
          },
        }
        trackFormSubmissionForGA4(trackingData)
        trackFormSubmissionServer({
          eventName: 'formSubmission',
          userId,
          anonymousUserId,
          data: trackingData,
        })
      }

      if (isSuccess && !!successCallback) {
        successCallback()
      }
    },
    [
      customSubject,
      formId,
      locale,
      projectId,
      propertyId,
      sendContactFormMutation,
      sendNewsletterRegistration,
      successCallback,
      trackFormSubmissionServer,
      trackFormSubmissionForGA4,
      type,
      userId,
    ]
  )
  return { submitFn, loading }
}
